import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { filterTokens, focusRing, webTransition, isWeb } from './mapFilterUi';

/**
 * REQ-104: one collapsible checklist ("Venue type: All", "Features: 2 selected").
 *
 * props: id, label, summary, count, open, onToggleOpen, rows: [{ key, label, checked, onPress, emphasis }],
 *        note, children (custom body, e.g. the capacity inputs), isDarkMode, reducedMotion
 */
export default function FilterChecklistDropdown({
  id,
  label,
  summary,
  count = 0,
  open = false,
  onToggleOpen,
  rows = [],
  note = null,
  children = null,
  isDarkMode = false,
  reducedMotion = false,
}) {
  const t = filterTokens(isDarkMode);
  const styles = makeStyles(t, isDarkMode);
  const bodyId = `venue-filter-${id}-body`;

  return (
    <View style={styles.wrap}>
      <Pressable
        onPress={onToggleOpen}
        accessibilityRole="button"
        accessibilityLabel={`${label}: ${summary}`}
        accessibilityState={{ expanded: open }}
        {...(isWeb ? { 'aria-controls': bodyId, 'aria-expanded': open } : {})}
        style={({ hovered, focused }) => [
          styles.header,
          hovered && styles.headerHover,
          webTransition(reducedMotion),
          focusRing(focused, isDarkMode),
        ]}
      >
        <Text style={styles.headerLabel} numberOfLines={1}>{label}</Text>
        <View style={styles.headerRight}>
          <Text style={[styles.summary, count > 0 && styles.summaryOn]} numberOfLines={1}>{summary}</Text>
          <Ionicons
            name="chevron-down"
            size={14}
            color={isDarkMode ? 'rgba(255,255,255,0.55)' : t.pal.mute}
            style={[
              { transform: [{ rotate: open ? '180deg' : '0deg' }] },
              webTransition(reducedMotion, 'transform'),
            ]}
          />
        </View>
      </Pressable>

      {open ? (
        <View nativeID={bodyId} style={styles.body}>
          {children}
          {rows.map((row) => (
            <Pressable
              key={row.key}
              onPress={row.onPress}
              accessibilityRole="checkbox"
              accessibilityLabel={row.label}
              accessibilityState={{ checked: row.checked }}
              {...(isWeb ? { 'aria-checked': row.checked } : {})}
              style={({ hovered, focused }) => [
                styles.row,
                hovered && styles.rowHover,
                webTransition(reducedMotion),
                focusRing(focused, isDarkMode),
              ]}
            >
              <View style={[styles.box, row.checked && styles.boxOn, webTransition(reducedMotion)]}>
                {row.checked ? <Ionicons name="checkmark" size={12} color="#fffffe" /> : null}
              </View>
              <Text style={[styles.rowLabel, row.emphasis && styles.rowLabelEmphasis]}>{row.label}</Text>
            </Pressable>
          ))}
          {note ? <Text style={styles.note}>{note}</Text> : null}
        </View>
      ) : null}
    </View>
  );
}

function makeStyles(t, isDarkMode) {
  return StyleSheet.create({
    wrap: {
      borderBottomWidth: 1,
      borderBottomColor: t.pal.lineSoft,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      minHeight: 48,
      paddingHorizontal: 4,
      borderRadius: 6,
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
    headerHover: { backgroundColor: t.hover },
    headerLabel: {
      flexShrink: 1,
      fontFamily: t.sans,
      fontSize: 14,
      fontWeight: '600',
      letterSpacing: -0.1,
      color: t.pal.ink,
    },
    headerRight: { flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 0 },
    summary: {
      fontFamily: t.mono,
      fontSize: 11,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: isDarkMode ? 'rgba(255,255,255,0.5)' : t.pal.muteSoft,
    },
    summaryOn: { color: t.greenText, fontWeight: '600' },
    body: { paddingBottom: 12, paddingHorizontal: 2, gap: 2 },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      minHeight: 36,
      paddingHorizontal: 6,
      borderRadius: 6,
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
    rowHover: { backgroundColor: t.hover },
    box: {
      width: 18,
      height: 18,
      borderRadius: 4,
      borderWidth: 1.5,
      borderColor: isDarkMode ? 'rgba(255,255,255,0.35)' : '#b9b9b3',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
    },
    boxOn: { backgroundColor: t.green, borderColor: t.green },
    rowLabel: {
      flex: 1,
      fontFamily: t.sans,
      fontSize: 13.5,
      color: isDarkMode ? 'rgba(255,255,255,0.86)' : t.pal.inkSoft,
    },
    rowLabelEmphasis: { fontWeight: '600' },
    note: {
      marginTop: 6,
      paddingHorizontal: 6,
      fontFamily: t.sans,
      fontSize: 12,
      lineHeight: 17,
      color: isDarkMode ? 'rgba(255,255,255,0.55)' : t.pal.mute,
    },
  });
}
